import { FetchableResourceStatus } from '~/_shared/types/fetchableResource/fetchableResource';
import { isResourceLoaded } from '~/_shared/types/fetchableResource/fetchableResource.helpers';
import type { ColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { compareSpreadsheetColumnIds } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { stringObjectEntries } from '~/_shared/utils/object/objectEnumerators';
import {
  isNullsy, notNullsy,
} from '~/_shared/utils/typeGuards';
import type {
  SpreadsheetDataBulkResponse, SpreadsheetDataErrorResponse,
} from '~/spreadsheet/spreadsheet.repository';
import { isSuccessBulkSpreadsheet } from '~/spreadsheet/typeguards';
import { generateGroupIdByName } from '~/store/spreadsheetData/grouping/spreadsheetData.grouping.helpers';
import {
  DataType, type GroupId,
} from '~/store/spreadsheetData/spreadsheetData.state';
import { createLoadedStatItem } from '~/store/stats/stats.factory';
import { ChartMetricType } from '../mapSettings/data/charts/mapSettingsCharts.types';
import {
  type BoundaryStatsRequestItem, type BoundaryStatsResponse, type BoundaryStatsResponseItem, StatsProcessingStatus,
} from './stats.repository';
import type {
  BoundaryStatItemDemographic, BoundaryStatItemDemographicMetricData, BoundaryStatItemLocationCount,
  BoundaryStatItemLocationCountMetricData, BoundaryStatItemMetricData, BoundaryStatItemSpreadsheetColumn,
  BoundaryStatItemSpreadsheetColumnMetricData, BoundaryStatsItem, StatItem, StatItemMetricData, StatsState,
} from './stats.state';

export const isStatsItemLoading = (statsItem: StatItem): boolean => (
  statsItem.data.status === FetchableResourceStatus.Loading
  || statsItem.data.status === FetchableResourceStatus.Requested
);

export const isStatsItemLoaded = (statsItem: StatItem): boolean => (
  isResourceLoaded(statsItem.data)
);

export const isStatsItemSyncing = (statsItem: StatItem): boolean => (
  isResourceLoaded(statsItem.data)
  && statsItem.data.value.status !== StatsProcessingStatus.Finished
);

export const isBoundaryStatItem = (metricData: StatItem): metricData is BoundaryStatsItem => (
  metricData.boundaryGroupId !== undefined
);

export const isBoundaryStatItemSpreadsheetColumn = (statItem: StatItem): statItem is BoundaryStatItemSpreadsheetColumn => (
  isBoundaryStatItem(statItem) && statItem.type === ChartMetricType.SpreadsheetColumn
);

export const isBoundaryStatItemLocationCount = (statItem: StatItem): statItem is BoundaryStatItemLocationCount => (
  isBoundaryStatItem(statItem) && statItem.type === ChartMetricType.LocationCount
);

export const isBoundaryStatItemDemographic = (statItem: StatItem): statItem is BoundaryStatItemDemographic => (
  isBoundaryStatItem(statItem) && statItem.type === ChartMetricType.Demographic
);

export const isBoundaryStatItemSpreadsheetColumnMetricData = (metricData: StatItemMetricData): metricData is BoundaryStatItemSpreadsheetColumnMetricData => (
  metricData.boundaryGroupId !== undefined && metricData.type === ChartMetricType.SpreadsheetColumn
);

export const isBoundaryStatItemLocationCountMetricData = (metricData: StatItemMetricData): metricData is BoundaryStatItemLocationCountMetricData => (
  metricData.boundaryGroupId !== undefined && metricData.type === ChartMetricType.LocationCount
);

export const isBoundaryStatItemDemographicMetricData = (metricData: StatItemMetricData): metricData is BoundaryStatItemDemographicMetricData => (
  metricData.boundaryGroupId !== undefined && metricData.type === ChartMetricType.Demographic
);

export const findBoundaryGroupStatItemSpreadsheetColumn = (
  boundaryGroupsStats: StatsState['boundaryGroupStats'],
  metricData: BoundaryStatItemSpreadsheetColumnMetricData,
): BoundaryStatItemSpreadsheetColumn | null => {
  const boundaryGroupStats = boundaryGroupsStats.get(metricData.boundaryGroupId);
  if (boundaryGroupStats) {
    return Array.from(boundaryGroupStats.values())
      .filter(isBoundaryStatItemSpreadsheetColumn)
      .find(stat => compareSpreadsheetColumnIds(stat.column, metricData.column)) ?? null;
  }
  return null;
};

export const findBoundaryGroupStatItemLocationCount = (
  boundaryGroupsStats: StatsState['boundaryGroupStats'],
  metricData: BoundaryStatItemLocationCountMetricData,
): BoundaryStatItemLocationCount | null => {
  const groupStats = boundaryGroupsStats.get(metricData.boundaryGroupId);
  if (groupStats) {
    return Array.from(groupStats.values())
      .filter(isBoundaryStatItemLocationCount)
      .find(stat => {
        // Handle total location count case (no column, no columnValue, has spreadsheetId)
        if (isNullsy(metricData.column)
          && isNullsy(stat.column)
          && isNullsy(metricData.columnValue)
          && isNullsy(stat.columnValue)
          && notNullsy(metricData.spreadsheetId)
        ) {
          return stat.spreadsheetId === metricData.spreadsheetId;
        }
        // Handle group column case (has column, may or may not have columnValue)
        if (notNullsy(metricData.column) && notNullsy(stat.column)) {
          return (
            compareSpreadsheetColumnIds(stat.column, metricData.column) &&
            stat.columnValue === metricData.columnValue
          );
        }
        return false;
      }) ?? null;
  }
  return null;
};

export const findBoundaryGroupStatItemDemographic = (
  boundaryGroupsStats: StatsState['boundaryGroupStats'],
  metricData: BoundaryStatItemDemographicMetricData,
): BoundaryStatItemDemographic | null => {
  const boundaryGroupStats = boundaryGroupsStats.get(metricData.boundaryGroupId);
  if (boundaryGroupStats) {
    return Array.from(boundaryGroupStats.values())
      .filter(isBoundaryStatItemDemographic)
      .find(stat => stat.demographicId === metricData.demographicId) ?? null;
  }
  return null;
};

export const boundaryStatsItemToBoundaryStatsRequestItem = (statsItem: BoundaryStatsItem): BoundaryStatsRequestItem => {
  const commonValues = {
    query_id: statsItem.id,
    boundary_group_id: statsItem.boundaryGroupId,
  };

  switch (statsItem.type) {
    case ChartMetricType.SpreadsheetColumn:
      return {
        ...commonValues,
        type: ChartMetricType.SpreadsheetColumn,
        spreadsheet_id: statsItem.column.spreadsheetId,
        metric: statsItem.column.columnId,
      };

    case ChartMetricType.LocationCount:
      return {
        ...commonValues,
        type: ChartMetricType.LocationCount,
        spreadsheet_id: statsItem.spreadsheetId ?? statsItem.column?.spreadsheetId ?? -1, // should never be -1
        metric: statsItem.column?.columnId ?? null,
        column_value: statsItem.columnValue,
      };

    case ChartMetricType.Demographic:
    default:
      return {
        ...commonValues,
        type: ChartMetricType.Demographic,
        metric: statsItem.demographicId,
      };
  }
};

export const findBoundaryGroupStatItem = (
  boundaryGroupsStats: StatsState['boundaryGroupStats'],
  metricData: BoundaryStatItemMetricData,
): BoundaryStatsItem | null => {
  if (isBoundaryStatItemSpreadsheetColumnMetricData(metricData)) {
    return findBoundaryGroupStatItemSpreadsheetColumn(boundaryGroupsStats, metricData);
  }
  if (isBoundaryStatItemDemographicMetricData(metricData)) {
    return findBoundaryGroupStatItemDemographic(boundaryGroupsStats, metricData);
  }
  if (isBoundaryStatItemLocationCountMetricData(metricData)) {
    return findBoundaryGroupStatItemLocationCount(boundaryGroupsStats, metricData);
  }
  return null;
};

export const setStatsItemToLoading = (statItem: StatItem): StatItem => {
  if (!isResourceLoaded(statItem.data)) {
    return {
      ...statItem,
      data: {
        status: FetchableResourceStatus.Loading,
      },
    };
  }

  if (isBoundaryStatItemSpreadsheetColumn(statItem) || isBoundaryStatItemLocationCount(statItem)) {
    return {
      ...statItem,
      data: {
        ...statItem.data,
        value: {
          ...statItem.data.value,
          status: StatsProcessingStatus.InProgress,
        },
      },
    };
  }

  if (isBoundaryStatItemDemographic(statItem)) {
    return {
      ...statItem,
      data: {
        ...statItem.data,
        value: {
          ...statItem.data.value,
          status: StatsProcessingStatus.InProgress,
        },
      },
    };
  }
  return statItem;
};

export type GroupColumnFormattedValuesLookup = Map<SpreadsheetId, Map<ColumnId, Map<GroupId, string>>>;

export const parseFormattedValuesResponse = (
  response: { readonly data: ReadonlyArray<SpreadsheetDataBulkResponse | SpreadsheetDataErrorResponse> }
): GroupColumnFormattedValuesLookup => {
  const lookup: GroupColumnFormattedValuesLookup = new Map();

  response.data.forEach(spreadsheetResponse => {
    if (!isSuccessBulkSpreadsheet(spreadsheetResponse)) {
      return;
    }

    const spreadsheetId = spreadsheetResponse.spreadsheet_id;
    const groupData = spreadsheetResponse.result?.columns_to_fetch?.[DataType.GROUP];

    if (!groupData) {
      return;
    }

    const columnsMap = new Map<ColumnId, Map<ColumnId, string>>();
    Object.entries(groupData).forEach(([columnId, columnData]) => {
      const uniqueValues = columnData.unique_values;
      const uniqueValuesFormatted = columnData.unique_values_formatted ?? uniqueValues;

      const valuesMap = new Map<GroupId, string>();
      uniqueValues.forEach((uniqueValue, index) => {
        valuesMap.set(generateGroupIdByName(uniqueValue), uniqueValuesFormatted[index] ?? uniqueValue);
      });

      columnsMap.set(columnId, valuesMap);
    });

    lookup.set(spreadsheetId, columnsMap);
  });

  return lookup;
};

export const processBoundaryStatsResponse = (
  response: BoundaryStatsResponse,
  queryItems: ReadonlyArray<BoundaryStatsRequestItem>,
  allStats: ReadonlyArray<BoundaryStatsItem>,
) => {
  const itemsToUpdate: BoundaryStatsItem[] = [];
  const requestsWithoutResponse: BoundaryStatsRequestItem[] = [];

  stringObjectEntries<string, BoundaryStatsResponseItem | null>(response.result).forEach(([queryId, queryItem]) => {
    const existingStat = allStats.find((stat) => stat.id === queryId);

    if (existingStat) {
      if (queryItem) {
        const newItem = createLoadedStatItem(queryId, queryItem);
        if (newItem) {
          itemsToUpdate.push(newItem);
        }
      }
      else { // if no response, we want to try to calculate
        const queryRequestItem = queryItems.find((item) => item.query_id === queryId);
        if (queryRequestItem) {
          requestsWithoutResponse.push(queryRequestItem);
        }
      }
    }
  });

  return {
    itemsToUpdate,
    requestsWithoutResponse,
  };
};
